import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Home } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { landingFor } from '../../lib/landing'

/**
 * Full-page 404. Mirrors the `MaintenanceScreen` layout so the two
 * full-page error surfaces feel like one family:
 *   - top-left brand logo
 *   - left column: big "Lost in space?" headline, short body copy,
 *     a primary "Take me home" CTA, secondary "Go back" link
 *   - right column: illustration loaded from `/not-found.png`
 *
 * The "home" target is role-aware via `landingFor` — signed-in users
 * land on their dashboard, signed-out visitors hit the public sign-in
 * page. Saves the user one extra hop from a generic landing page.
 *
 * The illustration is loaded from `public/not-found.png` (same
 * convention as the maintenance screen). Missing-file `onError`
 * hides the `<img>` so a deploy without the asset still renders
 * cleanly as a text-only screen.
 */
export function NotFoundPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const homeTarget = user ? landingFor(user.role) : '/sign-in'

  return (
    <div className="relative min-h-screen bg-[color:var(--color-bg)] flex flex-col overflow-hidden">
      {/* Ambient cosmic backdrop — DARK MODE ONLY. Two soft accent glows and
       *  a sparse star field give the "lost in space" theme real depth on the
       *  near-black canvas. `hidden dark:block` keeps the clean light design
       *  untouched; `pointer-events-none` so it never blocks the CTAs. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 hidden dark:block overflow-hidden"
      >
        <div
          className="absolute -top-40 right-[6%] h-[560px] w-[560px] rounded-full blur-3xl opacity-40"
          style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.18), transparent 70%)' }}
        />
        <div
          className="absolute -bottom-44 left-[2%] h-[460px] w-[460px] rounded-full blur-3xl opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.16), transparent 70%)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(1.5px 1.5px at 15% 20%, rgba(255,255,255,0.6), transparent),' +
              'radial-gradient(1px 1px at 42% 68%, rgba(255,255,255,0.4), transparent),' +
              'radial-gradient(1.5px 1.5px at 66% 14%, rgba(255,255,255,0.5), transparent),' +
              'radial-gradient(1px 1px at 82% 52%, rgba(255,255,255,0.35), transparent),' +
              'radial-gradient(1px 1px at 24% 84%, rgba(255,255,255,0.45), transparent),' +
              'radial-gradient(1.5px 1.5px at 91% 82%, rgba(255,255,255,0.4), transparent),' +
              'radial-gradient(1px 1px at 54% 38%, rgba(255,255,255,0.3), transparent),' +
              'radial-gradient(1px 1px at 8% 58%, rgba(255,255,255,0.4), transparent),' +
              'radial-gradient(1px 1px at 34% 10%, rgba(255,255,255,0.35), transparent)',
          }}
        />
      </div>

      {/* Body — two columns on lg+, stacked otherwise. Wider cap
       *  (max-w-7xl) and roomier vertical padding so the headline
       *  has air to breathe and the section fills more of the
       *  viewport, instead of feeling stranded in the middle. */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left column — text + actions. Wider max so the
           *  one-line headline "Lost in space?" has room to render
           *  without wrapping. */}
          <div className="max-w-2xl">
            {/* Small 404 eyebrow above the headline — keeps the
             *  technical number visible (useful for support / SEO)
             *  without making it the loudest thing on the page. */}
            <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--color-accent-strong)] bg-[color:var(--color-accent-bg)] border border-[color:var(--color-accent-border)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-accent)]" />
              Error 404
            </p>
            {/* `whitespace-nowrap` enforces the single-line render
             *  even at narrower viewport widths — the responsive
             *  font sizing (text-5xl → text-7xl) keeps it visually
             *  big without pushing past the column edge on tablet. */}
            <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[color:var(--color-ink-strong)] leading-[1.05] tracking-tight whitespace-nowrap">
              Lost in space?
            </h1>
            {/* Body copy bumped from text-base → text-lg so it pairs
             *  better with the larger headline above. Buttons and
             *  the footer line get the same one-step-up treatment
             *  for visual consistency. */}
            <p className="mt-6 text-lg text-[color:var(--color-ink-muted)] leading-relaxed max-w-lg">
              The page you&rsquo;re looking for doesn&rsquo;t exist, has been
              moved, or its link is broken. Let&rsquo;s get you back on
              track.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={homeTarget}
                className="taskmagix-btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-md text-base font-semibold cursor-pointer"
              >
                <Home size={18} />
                Take me home
              </Link>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-base font-medium bg-[color:var(--color-card)] text-[color:var(--color-ink)] border border-[color:var(--color-rule-strong)] hover:bg-[color:var(--color-card-hover)] hover:border-[color:var(--color-ink-muted)] cursor-pointer"
              >
                <ArrowLeft size={18} />
                Go back
              </button>
            </div>

            <p className="mt-10 text-sm text-[color:var(--color-ink-subtle)]">
              If you reached this page from a link inside TaskMagix, please
              let support know so we can fix it.
            </p>
          </div>

          {/* Right column — illustration. Drop the PNG at
           *  `public/not-found.png` to swap visuals. `onError` hides
           *  the `<img>` if the asset is missing, so the screen
           *  degrades gracefully to text-only.
           *
           *  `max-w-2xl` (672px) + `max-h-[640px]` gives the
           *  illustration real visual presence — it now reads as
           *  the primary subject of the right column instead of a
           *  modest accent next to the text. `object-contain`
           *  keeps the aspect ratio intact whatever the source PNG
           *  dimensions happen to be. */}
          <div className="hidden lg:flex items-center justify-center">
            <img
              src="/not-found.png"
              alt="Page not found"
              className="w-full max-w-2xl max-h-[640px] h-auto object-contain select-none"
              draggable={false}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
